import { useEffect, useState } from 'react';
import { API_BASE } from '../config/api';

export type BillingCurrency = 'USD' | 'IDR';
export type BillingPlanId = 'starter' | 'pro' | 'studio';
export type BillingCycle = 'monthly' | 'yearly';

export interface BillingPlanPrice {
  monthly_price: number | null;
  yearly_price: number | null;
  monthly_credits: unknown;
  yearly_credits: unknown;
}

export interface BillingTopupPack {
  amount: number;
  credit_type: string;
  usd_price: number | null;
  idr_price: number | null;
}

export interface BillingContext {
  currency: BillingCurrency;
  provider: string;
  country_code: string | null;
  plans: Record<string, BillingPlanPrice>;
  midtrans_prices_idr: Record<string, number>;
  topup: Record<string, BillingTopupPack>;
  one_time_period: boolean;
  /** USD/IDR rate used to derive the Rupiah figures, when Rupiah is shown. */
  fx_rate: number | null;
  /** Attribution the rate provider requires when its rate is displayed. */
  fx_attribution: string;
  /** True when the server quoted from a cached rate instead of a fresh one. */
  fx_rate_stale: boolean;
}

/** Mirrors the stored defaults so prices never render empty while loading. */
export const FALLBACK_BILLING: BillingContext = {
  currency: 'USD',
  provider: 'lemonsqueezy',
  country_code: null,
  plans: {
    starter: { monthly_price: 5, yearly_price: 50, monthly_credits: 200, yearly_credits: 2400 },
    pro: { monthly_price: 10, yearly_price: 99, monthly_credits: 500, yearly_credits: 6000 },
    studio: { monthly_price: 25, yearly_price: 240, monthly_credits: 1500, yearly_credits: 18000 }
  },
  midtrans_prices_idr: {},
  fx_rate: null,
  fx_attribution: '',
  fx_rate_stale: false,
  topup: {
    vec_200: { amount: 200, credit_type: 'vector', usd_price: 4, idr_price: null },
    vec_500: { amount: 500, credit_type: 'vector', usd_price: 9, idr_price: null },
    vec_1500: { amount: 1500, credit_type: 'vector', usd_price: 24, idr_price: null },
    ai_500: { amount: 500, credit_type: 'ai', usd_price: 4, idr_price: null },
    ai_1500: { amount: 1500, credit_type: 'ai', usd_price: 10, idr_price: null },
    ai_4000: { amount: 4000, credit_type: 'ai', usd_price: 22, idr_price: null }
  },
  one_time_period: false
};

export const formatUsd = (value: number | null | undefined): string | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
};

export const formatIdr = (value: number | null | undefined): string | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return `Rp ${Math.round(value).toLocaleString('id-ID')}`;
};

/**
 * Price for one plan/cycle as it should be displayed.
 *
 * Rupiah is used only when the server reports IDR for this visitor, which means
 * their country routes to a ready Midtrans checkout and a USD/IDR rate was
 * available. The server derives those figures from the same USD list price, so
 * both markets pay the same real amount; the client never converts on its own.
 * A missing figure falls back to USD so the card never advertises an amount the
 * gateway would not charge.
 */
export const planPriceText = (
  context: BillingContext,
  plan: BillingPlanId,
  cycle: BillingCycle
): { text: string; currency: BillingCurrency } => {
  if (context.currency === 'IDR') {
    const idr = formatIdr(context.midtrans_prices_idr[`${plan}_${cycle}`]);
    if (idr) return { text: idr, currency: 'IDR' };
  }
  const usd = formatUsd(context.plans[plan]?.[cycle === 'monthly' ? 'monthly_price' : 'yearly_price']);
  return { text: usd ?? '—', currency: 'USD' };
};

export const creditCountFor = (context: BillingContext, plan: BillingPlanId, cycle: BillingCycle): number | null => {
  const value = cycle === 'monthly' ? context.plans[plan]?.monthly_credits : context.plans[plan]?.yearly_credits;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const planPriceWithCycle = (
  context: BillingContext,
  plan: BillingPlanId,
  cycle: BillingCycle,
  labels: { month: string; year: string }
): string => {
  const { text, currency } = planPriceText(context, plan, cycle);
  const suffix = cycle === 'yearly' ? labels.year : labels.month;
  const period = currency === 'IDR' ? (cycle === 'yearly' ? '/tahun' : '/bulan') : suffix;
  return `${text}${period}`;
};

export const unitCostText = (
  context: BillingContext,
  plan: BillingPlanId,
  cycle: BillingCycle,
  perCreditLabel: string
): string => {
  const credits = creditCountFor(context, plan, cycle);
  if (!credits) return '';
  if (context.currency === 'IDR') {
    const idr = context.midtrans_prices_idr[`${plan}_${cycle}`];
    if (!idr) return '';
    return `(Rp ${Math.round(idr / credits).toLocaleString('id-ID')} ${perCreditLabel})`;
  }
  const usd = context.plans[plan]?.[cycle === 'monthly' ? 'monthly_price' : 'yearly_price'];
  if (typeof usd !== 'number' || !Number.isFinite(usd) || usd <= 0) return '';
  const per = usd / credits;
  return `($${per < 0.1 ? per.toFixed(3) : per.toFixed(2)} ${perCreditLabel})`;
};

export interface TopupPriceText {
  /** Pack price in the visitor's currency, ready to render. */
  text: string;
  currency: BillingCurrency;
  /** Price of a single credit in the same currency, or '' when unknown. */
  perCreditValue: string;
}

/**
 * Price for one credit pack as it should be displayed.
 *
 * Mirrors the plan rules: Rupiah only when the server reports IDR and that pack
 * has a configured IDR amount, otherwise the USD catalog price. `perCreditValue`
 * is returned unformatted so the existing localized "per credit" copy can wrap
 * it in either currency.
 */
export const topupPriceText = (context: BillingContext, packId: string): TopupPriceText => {
  const pack = context.topup?.[packId];
  const packAmount = Number(pack?.amount);
  const credits = Number.isFinite(packAmount) && packAmount > 0 ? packAmount : null;
  const usdValue = pack?.usd_price;
  const idrValue = pack?.idr_price;

  if (context.currency === 'IDR') {
    const idr = formatIdr(idrValue);
    if (idr) {
      const perCreditValue =
        credits && typeof idrValue === 'number'
          ? `Rp ${Math.round(idrValue / credits).toLocaleString('id-ID')}`
          : '';
      return { text: idr, currency: 'IDR', perCreditValue };
    }
  }

  const usd = formatUsd(usdValue);
  if (!usd) return { text: '—', currency: 'USD', perCreditValue: '' };
  const perCreditValue =
    credits && typeof usdValue === 'number'
      ? `$${(usdValue / credits) < 0.1 ? (usdValue / credits).toFixed(3) : (usdValue / credits).toFixed(2)}`
      : '';
  return { text: usd, currency: 'USD', perCreditValue };
};

export function useBillingContext(): BillingContext {
  const [context, setContext] = useState<BillingContext>(FALLBACK_BILLING);

  useEffect(() => {
    // Display only. The server recomputes the chargeable amount at checkout, so
    // this response can never change what a user is actually billed.
    let cancelled = false;
    const controller = new AbortController();
    fetch(`${API_BASE}/api/public/billing-context`, { signal: controller.signal, credentials: 'omit' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || (data.currency !== 'USD' && data.currency !== 'IDR')) return;
        setContext({
          currency: data.currency,
          provider: String(data.provider || 'lemonsqueezy'),
          country_code: data.country_code ?? null,
          plans: data.plans && typeof data.plans === 'object' ? data.plans : FALLBACK_BILLING.plans,
          midtrans_prices_idr:
            data.midtrans_prices_idr && typeof data.midtrans_prices_idr === 'object' ? data.midtrans_prices_idr : {},
          topup: data.topup && typeof data.topup === 'object' ? data.topup : FALLBACK_BILLING.topup,
          one_time_period: Boolean(data.one_time_period),
          fx_rate: typeof data.fx_rate === 'number' && Number.isFinite(data.fx_rate) ? data.fx_rate : null,
          fx_attribution: typeof data.fx_attribution === 'string' ? data.fx_attribution : '',
          fx_rate_stale: Boolean(data.fx_rate_stale)
        });
      })
      .catch(() => { /* keep the USD fallback */ });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return context;
}
